import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useSnackbar } from "notistack";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import EntityTable, { RowActionMenu } from "components/EntityTable";
import ConfirmDialog from "components/ConfirmDialog";
import SidePanel from "components/SidePanel";
import CategoryForm from "components/content/CategoryForm";
import usePaginatedResource from "hooks/usePaginatedResource";
import useDebounce from "hooks/useDebounce";
import { listCategoriesPaged, deleteCategory } from "api/endpoints/categories";

// Website-scoped content categories management screen.
function CategoriesListPage() {
  const { websiteId } = useOutletContext();
  const { enqueueSnackbar } = useSnackbar();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  const [panel, setPanel] = useState({ open: false, category: null });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCategories = useCallback(
    (pageParams) => {
      if (!websiteId) return { items: [], total: 0 };
      return listCategoriesPaged(websiteId, { ...pageParams, search: debouncedSearch });
    },
    [websiteId, debouncedSearch]
  );

  const { rows, total, loading, page, pageSize, setPage, setPageSize, refresh } =
    usePaginatedResource(fetchCategories);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, setPage]);

  // Resolve parent_id → name from the rows currently on screen.
  const parentNames = useMemo(
    () => Object.fromEntries(rows.map((row) => [row.content_category_id, row.name])),
    [rows]
  );

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteCategory(websiteId, confirmDeleteId);
      enqueueSnackbar("Category deleted", { variant: "success" });
      refresh();
    } catch {
      enqueueSnackbar("Failed to delete category", { variant: "error" });
    } finally {
      setDeleting(false);
      setConfirmDeleteId(null);
    }
  };

  const openCreate = () => setPanel({ open: true, category: null });
  const openEdit = (category) => setPanel({ open: true, category });
  const closePanel = () => setPanel({ open: false, category: null });

  const columns = [
    { field: "name", headerName: "Name", flex: 1, minWidth: 160 },
    {
      field: "slug",
      headerName: "Slug",
      flex: 1,
      minWidth: 160,
      renderCell: ({ value }) => value || "—",
    },
    {
      field: "parent_id",
      headerName: "Parent",
      flex: 1,
      minWidth: 140,
      renderCell: ({ value }) => parentNames[value] || "—",
    },
    {
      field: "sort_order",
      headerName: "Sort order",
      width: 120,
      renderCell: ({ value }) => (value != null ? value : "—"),
    },
    {
      field: "actions",
      headerName: "",
      width: 64,
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <RowActionMenu
          actions={[
            { label: "Edit", icon: "edit", onClick: () => openEdit(row) },
            {
              label: "Delete",
              icon: "delete",
              color: "error",
              onClick: () => setConfirmDeleteId(row.content_category_id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <MDBox>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        mb={3}
      >
        <MDTypography variant="h5" fontWeight="bold">
          Categories
        </MDTypography>
        <MDButton variant="gradient" color="info" startIcon={<Icon>add</Icon>} onClick={openCreate}>
          New Category
        </MDButton>
      </MDBox>

      <Card>
        <MDBox p={3}>
          <MDBox mb={2} maxWidth={320}>
            <MDInput
              fullWidth
              placeholder="Search by name or slug"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{ endAdornment: <Icon fontSize="small">search</Icon> }}
            />
          </MDBox>
          <EntityTable
            rows={rows}
            columns={columns}
            getRowId={(row) => row.content_category_id}
            loading={loading}
            paginationMode="server"
            rowCount={total}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onRowClick={({ row }) => openEdit(row)}
          />
        </MDBox>
      </Card>

      <SidePanel
        open={panel.open}
        onClose={closePanel}
        title={panel.category ? "Edit Category" : "New Category"}
        subtitle="Organize this website's content into categories"
        width={760}
      >
        {panel.open && (
          <CategoryForm
            websiteId={websiteId}
            category={panel.category}
            onSaved={() => {
              closePanel();
              refresh();
            }}
          />
        )}
      </SidePanel>

      <ConfirmDialog
        open={confirmDeleteId !== null}
        title="Delete Category"
        message="Are you sure you want to delete this category?"
        confirmText="Delete"
        loading={deleting}
        onConfirm={confirmDelete}
        onClose={() => setConfirmDeleteId(null)}
      />
    </MDBox>
  );
}

export default CategoriesListPage;
